export interface SignedRegistryConfig {
    readonly endpoint: string;
    readonly keyId: string;
    readonly publicKeyBase64: string;
    readonly bundledSequence: number;
    readonly allowLocalhostHttp?: boolean;
}

export function parseSignedRegistryConfig(value: unknown): SignedRegistryConfig | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)
        || !('endpoint' in value) || !('keyId' in value)
        || !('publicKeyBase64' in value) || !('bundledSequence' in value)) return null;
    const { endpoint, keyId, publicKeyBase64, bundledSequence } = value;
    const allowLocalhostHttp = 'allowLocalhostHttp' in value ? value.allowLocalhostHttp : undefined;
    if (typeof endpoint !== 'string' || endpoint.trim() === ''
        || typeof keyId !== 'string' || keyId.trim() === ''
        || typeof publicKeyBase64 !== 'string' || publicKeyBase64.trim() === ''
        || typeof bundledSequence !== 'number' || !Number.isSafeInteger(bundledSequence) || bundledSequence < 0
        || (allowLocalhostHttp !== undefined && typeof allowLocalhostHttp !== 'boolean')) return null;
    return { endpoint, keyId, publicKeyBase64, bundledSequence,
        ...(allowLocalhostHttp === undefined ? {} : { allowLocalhostHttp }) };
}

interface InputError { readonly field: string; readonly code: string; readonly message: string; }
type Decoded<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly errors: readonly InputError[] };
type Bounds = readonly [number, number];
type InputRecord = Readonly<Record<string, unknown>>;
interface Metadata {
    readonly calculatorId: string;
    readonly version: string;
    readonly effectiveFrom: string;
    readonly lastVerifiedAt: string;
    readonly sources: readonly { readonly id: string; readonly title: string; readonly publisher: string; readonly url: string; readonly verifiedAt?: string }[];
    readonly notes?: readonly string[];
}
interface Calculation { readonly ok: boolean; readonly metadata: Metadata; }
interface CoreConfig { readonly metadata: Metadata; }
interface LpgConfig extends CoreConfig { readonly maxAbsoluteInput: number; }
interface FuelConfig extends CoreConfig { readonly distanceKm: Bounds; readonly consumptionL100Km: Bounds; readonly pricePerLitre: Bounds; }
interface ProfitConfig extends CoreConfig { readonly monetaryInput: Bounds; readonly targetMarginPercent: Bounds; readonly markupPercent: Bounds; }
interface BmrConfig extends CoreConfig { readonly ageYears: Bounds; readonly heightCm: Bounds; readonly weightKg: Bounds; }
interface IdealWeightConfig extends CoreConfig { readonly heightCm: Bounds; readonly optionalCurrentWeightKg: Bounds; }
interface LpgInput {
    readonly conversionCost: number; readonly gasolineConsumptionL100Km: number; readonly lpgConsumptionL100Km: number;
    readonly gasolinePricePerLitre: number; readonly lpgPricePerLitre: number; readonly monthlyDistanceKm: number; readonly extraOneOffCost: number;
}
interface FuelInput {
    readonly distanceKm: number; readonly consumptionL100Km: number; readonly pricePerLitre: number;
    readonly fuelType: 'benzin' | 'motorin' | 'lpg'; readonly direction: 'tekYon' | 'gidisDonus';
}
type ProfitInput = { readonly cost: number } & (
    { readonly mode: 'targetSaleMargin'; readonly targetMarginPercent: number }
    | { readonly mode: 'costMarkup'; readonly markupPercent: number }
    | { readonly mode: 'analyzeSale'; readonly salePrice: number }
);
interface BmrInput { readonly sex: 'erkek' | 'kadin'; readonly formula: 'mifflin' | 'harris'; readonly ageYears: number; readonly heightCm: number; readonly weightKg: number; }
interface IdealWeightInput { readonly sex: 'erkek' | 'kadin'; readonly heightCm: number; readonly currentWeightKg?: number; }
interface CoreEndpoint<TInput, TConfig> {
    validateConfig(config: TConfig): { readonly errors: readonly InputError[]; readonly metadata: Metadata };
    calculate(input: TInput, metadata: Metadata): Calculation;
}
export interface EverydayHealthCore {
    readonly lpg: CoreEndpoint<LpgInput, LpgConfig>;
    readonly fuel: CoreEndpoint<FuelInput, FuelConfig>;
    readonly profit: CoreEndpoint<ProfitInput, ProfitConfig>;
    readonly bmr: CoreEndpoint<BmrInput, BmrConfig>;
    readonly idealWeight: CoreEndpoint<IdealWeightInput, IdealWeightConfig>;
}

function numberInput(field: string, value: unknown, bounds: Bounds): Decoded<number> {
    const [minimum, maximum] = bounds;
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return { ok: false, errors: [{ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` }] };
    }
    if (value < minimum || value > maximum) {
        return { ok: false, errors: [{ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen aralığın dışındadır.` }] };
    }
    return { ok: true, value };
}

function choiceInput<T extends string>(field: string, value: unknown, choices: readonly T[], message: string): Decoded<T> {
    for (const choice of choices) if (value === choice) return { ok: true, value: choice };
    return { ok: false, errors: [{ field, code: 'UNSUPPORTED_VALUE', message }] };
}

function inputErrors(...values: readonly Decoded<unknown>[]): readonly InputError[] {
    return values.flatMap(value => value.ok ? [] : value.errors);
}

export function decodeLpgInput(input: InputRecord, config: LpgConfig): Decoded<LpgInput> {
    const bounds: Bounds = [1e-9, config.maxAbsoluteInput];
    const conversionCost = numberInput('conversionCost', input.conversionCost, bounds);
    const gasolineConsumptionL100Km = numberInput('gasolineConsumptionL100Km', input.gasolineConsumptionL100Km, bounds);
    const lpgConsumptionL100Km = numberInput('lpgConsumptionL100Km', input.lpgConsumptionL100Km, bounds);
    const gasolinePricePerLitre = numberInput('gasolinePricePerLitre', input.gasolinePricePerLitre, bounds);
    const lpgPricePerLitre = numberInput('lpgPricePerLitre', input.lpgPricePerLitre, bounds);
    const monthlyDistanceKm = numberInput('monthlyDistanceKm', input.monthlyDistanceKm, bounds);
    const extraOneOffCost = numberInput('extraOneOffCost', input.extraOneOffCost ?? 0, [0, config.maxAbsoluteInput]);
    if (!conversionCost.ok || !gasolineConsumptionL100Km.ok || !lpgConsumptionL100Km.ok
        || !gasolinePricePerLitre.ok || !lpgPricePerLitre.ok || !monthlyDistanceKm.ok || !extraOneOffCost.ok) {
        return { ok: false, errors: inputErrors(conversionCost, gasolineConsumptionL100Km, lpgConsumptionL100Km, gasolinePricePerLitre, lpgPricePerLitre, monthlyDistanceKm, extraOneOffCost) };
    }
    return { ok: true, value: { conversionCost: conversionCost.value, gasolineConsumptionL100Km: gasolineConsumptionL100Km.value,
        lpgConsumptionL100Km: lpgConsumptionL100Km.value, gasolinePricePerLitre: gasolinePricePerLitre.value,
        lpgPricePerLitre: lpgPricePerLitre.value, monthlyDistanceKm: monthlyDistanceKm.value, extraOneOffCost: extraOneOffCost.value } };
}

export function decodeFuelInput(input: InputRecord, config: FuelConfig): Decoded<FuelInput> {
    const distanceKm = numberInput('distanceKm', input.distanceKm, config.distanceKm);
    const consumptionL100Km = numberInput('consumptionL100Km', input.consumptionL100Km, config.consumptionL100Km);
    const pricePerLitre = numberInput('pricePerLitre', input.pricePerLitre, config.pricePerLitre);
    const fuelType = choiceInput('fuelType', input.fuelType, ['benzin', 'motorin', 'lpg'], 'Yakıt türü desteklenmiyor.');
    const direction = choiceInput('direction', input.direction, ['tekYon', 'gidisDonus'], 'Yol yönü desteklenmiyor.');
    if (!distanceKm.ok || !consumptionL100Km.ok || !pricePerLitre.ok || !fuelType.ok || !direction.ok) {
        return { ok: false, errors: inputErrors(distanceKm, consumptionL100Km, pricePerLitre, fuelType, direction) };
    }
    return { ok: true, value: { distanceKm: distanceKm.value, consumptionL100Km: consumptionL100Km.value,
        pricePerLitre: pricePerLitre.value, fuelType: fuelType.value, direction: direction.value } };
}

export function decodeProfitInput(input: InputRecord, config: ProfitConfig, mode: unknown): Decoded<ProfitInput> {
    const cost = numberInput('cost', input.cost, config.monetaryInput);
    const field = mode === 'targetSaleMargin' ? 'targetMarginPercent' : mode === 'costMarkup' ? 'markupPercent' : mode === 'analyzeSale' ? 'salePrice' : null;
    if (field === null) {
        return { ok: false, errors: [...inputErrors(cost), { field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Kâr marjı hesap türü desteklenmiyor.' }] };
    }
    const amount: Decoded<number> = Object.prototype.hasOwnProperty.call(input, field)
        ? numberInput(field, input[field], field === 'targetMarginPercent' ? config.targetMarginPercent : field === 'markupPercent' ? config.markupPercent : config.monetaryInput)
        : { ok: false, errors: [{ field, code: 'REQUIRED', message: 'Seçilen hesap türü için bu değer gereklidir.' }] };
    if (!cost.ok || !amount.ok) return { ok: false, errors: inputErrors(cost, amount) };
    if (mode === 'targetSaleMargin') return { ok: true, value: { mode, cost: cost.value, targetMarginPercent: amount.value } };
    if (mode === 'costMarkup') return { ok: true, value: { mode, cost: cost.value, markupPercent: amount.value } };
    if (mode === 'analyzeSale') return { ok: true, value: { mode, cost: cost.value, salePrice: amount.value } };
    throw new Error('Kâr marjı hesap türü doğrulanamadı.');
}

export function decodeBmrInput(input: InputRecord, config: BmrConfig): Decoded<BmrInput> {
    const sex = choiceInput('sex', input.sex, ['erkek', 'kadin'], 'Cinsiyet seçimi desteklenmiyor.');
    const ageYears = numberInput('ageYears', input.ageYears, config.ageYears);
    const heightCm = numberInput('heightCm', input.heightCm, config.heightCm);
    const weightKg = numberInput('weightKg', input.weightKg, config.weightKg);
    const formula = choiceInput('formula', input.formula, ['mifflin', 'harris'], 'BMR formülü desteklenmiyor.');
    if (!sex.ok || !ageYears.ok || !heightCm.ok || !weightKg.ok || !formula.ok) {
        return { ok: false, errors: inputErrors(sex, ageYears, heightCm, weightKg, formula) };
    }
    return { ok: true, value: { sex: sex.value, ageYears: ageYears.value, heightCm: heightCm.value, weightKg: weightKg.value, formula: formula.value } };
}

export function decodeIdealWeightInput(input: InputRecord, config: IdealWeightConfig): Decoded<IdealWeightInput> {
    const sex = choiceInput('sex', input.sex, ['erkek', 'kadin'], 'Cinsiyet seçimi desteklenmiyor.');
    const heightCm = numberInput('heightCm', input.heightCm, config.heightCm);
    const weight = input.currentWeightKg;
    const currentWeightKg: Decoded<number | undefined> = weight === undefined || weight === null ? { ok: true, value: undefined }
        : numberInput('currentWeightKg', weight, config.optionalCurrentWeightKg);
    if (!sex.ok || !heightCm.ok || !currentWeightKg.ok) return { ok: false, errors: inputErrors(sex, heightCm, currentWeightKg) };
    return { ok: true, value: { sex: sex.value, heightCm: heightCm.value,
        ...(currentWeightKg.value === undefined ? {} : { currentWeightKg: currentWeightKg.value }) } };
}

let everydayHealthCore: EverydayHealthCore | null = null;

export function installEverydayHealthCore(core: EverydayHealthCore): void {
    if (everydayHealthCore) throw new Error('Günlük/sağlık çekirdeği zaten bağlı.');
    everydayHealthCore = core;
}

function coreRuntime(): EverydayHealthCore {
    if (!everydayHealthCore) throw new Error('Günlük/sağlık çekirdeği bağlı değil.');
    return everydayHealthCore;
}

function calculateChecked<TInput extends object, TConfig>(endpoint: CoreEndpoint<TInput, TConfig>, decode: (input: InputRecord, config: TConfig) => Decoded<TInput>, input: InputRecord, config: TConfig) {
    const checked = endpoint.validateConfig(config);
    if (checked.errors.length) return { ok: false, errors: checked.errors, metadata: checked.metadata };
    const decoded = decode(input, config);
    if (!decoded.ok) return { ok: false, errors: decoded.errors, metadata: checked.metadata };
    for (const field of Object.keys(decoded.value)) {
        if (!Object.prototype.propertyIsEnumerable.call(input, field)) Object.defineProperty(decoded.value, field, { enumerable: false });
    }
    return endpoint.calculate(decoded.value, checked.metadata);
}

export function calculateCheckedLpgPayback(input: InputRecord, config: LpgConfig) {
    return calculateChecked(coreRuntime().lpg, decodeLpgInput, input, config);
}
export function calculateCheckedFuelConsumption(input: InputRecord, config: FuelConfig) {
    return calculateChecked(coreRuntime().fuel, decodeFuelInput, input, config);
}
export function calculateCheckedProfitMargin(input: InputRecord, config: ProfitConfig, mode: unknown) {
    return calculateChecked(coreRuntime().profit, (input, config) => decodeProfitInput(input, config, mode), input, config);
}
export function calculateCheckedBmr(input: InputRecord, config: BmrConfig) {
    return calculateChecked(coreRuntime().bmr, decodeBmrInput, input, config);
}
export function calculateCheckedIdealWeight(input: InputRecord, config: IdealWeightConfig) {
    return calculateChecked(coreRuntime().idealWeight, decodeIdealWeightInput, input, config);
}
